import {
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    Checkbox,
    Collapsible,
    Divider,
    EmptyState,
    FormLayout,
    IndexTable,
    InlineStack,
    Link,
    Select,
    Spinner,
    Text,
    TextField,
} from "@shopify/polaris";
import { PlusIcon } from "@shopify/polaris-icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import agent from "../../app/api/agent";
import { DataSyncStatus } from "../../app/models/DataSync";
import {
    ShopifyMetafieldAttributeOption,
    ShopifyMetafieldAttributeOwnerType,
    ShopifyMetafieldAttributeUpsert,
    ShopifyMetafieldDefinitionOption,
} from "../../app/models/ShopifyMetafieldAttribute";
import productAttributesEmptyStateImage from "../../assets/dashboard/polaris-attributes.svg";
import { SettingsCard } from "./SettingsControls";

export type MetafieldAttributesEditorStatus = {
    hasChanges: boolean;
    loading: boolean;
    saving: boolean;
    save?: () => Promise<void>;
    discard?: () => void;
};

type MetafieldAttributesSettingsProps = {
    onStatusChange?: (status: MetafieldAttributesEditorStatus) => void;
    onOpenShopifySync?: () => void;
};

type DraftAttribute = Omit<ShopifyMetafieldAttributeOption, "id"> & {
    id?: number | null;
};

type AttributeForm = {
    id?: number | null;
    ownerType: ShopifyMetafieldAttributeOwnerType;
    namespace: string;
    key: string;
    name: string;
    type: string;
    shopifyDefinitionId: string;
    description: string;
    isEnabled: boolean;
    displayOrder: string;
};

type AttributeModalMode = "add" | "edit";

const emptyForm: AttributeForm = {
    id: null,
    ownerType: "ProductVariant",
    namespace: "",
    key: "",
    name: "",
    type: "single_line_text_field",
    shopifyDefinitionId: "",
    description: "",
    isEnabled: true,
    displayOrder: "0",
};

const ownerTypeOptions = [
    { label: "Variant metafield", value: "ProductVariant" },
    { label: "Product metafield", value: "Product" },
];

const ownerTypeLabel = (ownerType: ShopifyMetafieldAttributeOwnerType) =>
    ownerType === "ProductVariant" ? "Variant metafield" : "Product metafield";

const pathKey = (ownerType: ShopifyMetafieldAttributeOwnerType, namespace: string, key: string) =>
    `${ownerType}|${namespace.trim().toLowerCase()}|${key.trim().toLowerCase()}`;

const attributeIdentity = (attribute: DraftAttribute) =>
    attribute.id ? `id:${attribute.id}` : `path:${pathKey(attribute.ownerType, attribute.namespace, attribute.key)}`;

const formatDateTime = (value?: string | null) => {
    if (!value) return "-";

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

const cloneAttributes = (attributes: DraftAttribute[]): DraftAttribute[] =>
    attributes.map((attribute) => ({
        ...attribute,
        values: attribute.values ? [...attribute.values] : [],
    }));

const normalizeAttributeSnapshot = (attribute: DraftAttribute) => ({
    id: attribute.id ?? null,
    ownerType: attribute.ownerType,
    namespace: attribute.namespace.trim(),
    key: attribute.key.trim(),
    name: attribute.name.trim(),
    type: attribute.type?.trim() || "single_line_text_field",
    shopifyDefinitionId: attribute.shopifyDefinitionId || "",
    description: attribute.description || "",
    isEnabled: !!attribute.isEnabled,
    displayOrder: Number(attribute.displayOrder) || 0,
});

const attributeSnapshot = (attributes: DraftAttribute[]) => JSON.stringify(
    attributes
        .map(normalizeAttributeSnapshot)
        .sort((left, right) =>
            (left.id ?? 0) - (right.id ?? 0)
            || left.ownerType.localeCompare(right.ownerType)
            || left.namespace.localeCompare(right.namespace)
            || left.key.localeCompare(right.key)
        )
);

const attributeToForm = (attribute: DraftAttribute): AttributeForm => ({
    id: attribute.id,
    ownerType: attribute.ownerType,
    namespace: attribute.namespace,
    key: attribute.key,
    name: attribute.name,
    type: attribute.type || "single_line_text_field",
    shopifyDefinitionId: attribute.shopifyDefinitionId || "",
    description: attribute.description || "",
    isEnabled: attribute.isEnabled,
    displayOrder: String(attribute.displayOrder ?? 0),
});

const formToPayload = (form: AttributeForm): ShopifyMetafieldAttributeUpsert => ({
    id: form.id,
    ownerType: form.ownerType,
    namespace: form.namespace.trim(),
    key: form.key.trim(),
    name: form.name.trim() || `${form.namespace.trim()}.${form.key.trim()}`,
    type: form.type.trim() || "single_line_text_field",
    shopifyDefinitionId: form.shopifyDefinitionId.trim(),
    description: form.description.trim(),
    isEnabled: form.isEnabled,
    displayOrder: Number.parseInt(form.displayOrder, 10) || 0,
});

const attributeToPayload = (attribute: DraftAttribute): ShopifyMetafieldAttributeUpsert => ({
    id: attribute.id,
    ownerType: attribute.ownerType,
    namespace: attribute.namespace.trim(),
    key: attribute.key.trim(),
    name: attribute.name.trim() || `${attribute.namespace.trim()}.${attribute.key.trim()}`,
    type: attribute.type?.trim() || "single_line_text_field",
    shopifyDefinitionId: attribute.shopifyDefinitionId || "",
    description: attribute.description || "",
    isEnabled: attribute.isEnabled,
    displayOrder: Number(attribute.displayOrder) || 0,
});

const payloadToDraftAttribute = (
    payload: ShopifyMetafieldAttributeUpsert,
    existingAttribute?: DraftAttribute
): DraftAttribute => ({
    id: payload.id ?? existingAttribute?.id ?? null,
    ownerType: payload.ownerType,
    namespace: payload.namespace,
    key: payload.key,
    name: payload.name,
    type: payload.type || "single_line_text_field",
    shopifyDefinitionId: payload.shopifyDefinitionId || "",
    description: payload.description || "",
    isEnabled: payload.isEnabled,
    displayOrder: payload.displayOrder,
    lastIndexedAt: existingAttribute?.lastIndexedAt,
    valueCount: existingAttribute?.valueCount ?? 0,
    values: existingAttribute?.values ?? [],
});

const definitionToDraftAttribute = (
    definition: ShopifyMetafieldDefinitionOption,
    existingAttribute: DraftAttribute | undefined,
    displayOrder: number
): DraftAttribute => ({
    id: existingAttribute?.id ?? null,
    ownerType: definition.ownerType,
    namespace: definition.namespace,
    key: definition.key,
    name: definition.name || `${definition.namespace}.${definition.key}`,
    type: definition.type || "single_line_text_field",
    shopifyDefinitionId: definition.shopifyDefinitionId || "",
    description: definition.description || "",
    isEnabled: true,
    displayOrder: existingAttribute?.displayOrder ?? displayOrder,
    lastIndexedAt: existingAttribute?.lastIndexedAt,
    valueCount: existingAttribute?.valueCount ?? 0,
    values: existingAttribute?.values ?? [],
});

const upsertDraftAttribute = (attributes: DraftAttribute[], nextAttribute: DraftAttribute) => {
    let replaced = false;
    const nextPath = pathKey(nextAttribute.ownerType, nextAttribute.namespace, nextAttribute.key);

    const nextAttributes = attributes.map((attribute) => {
        const sameId = !!nextAttribute.id && attribute.id === nextAttribute.id;
        const sameNewPath = !nextAttribute.id && pathKey(attribute.ownerType, attribute.namespace, attribute.key) === nextPath;

        if (!sameId && !sameNewPath) return attribute;

        replaced = true;
        return {
            ...attribute,
            ...nextAttribute,
            values: nextAttribute.values ?? attribute.values ?? [],
        };
    });

    if (!replaced) {
        nextAttributes.push(nextAttribute);
    }

    return nextAttributes.sort((left, right) =>
        (left.displayOrder ?? 0) - (right.displayOrder ?? 0)
        || (left.name || "").localeCompare(right.name || "")
    );
};

const MetafieldAttributesSettings: React.FC<MetafieldAttributesSettingsProps> = ({ onStatusChange, onOpenShopifySync }) => {
    const [attributes, setAttributes] = useState<DraftAttribute[]>([]);
    const [baselineAttributes, setBaselineAttributes] = useState<DraftAttribute[]>([]);
    const [definitions, setDefinitions] = useState<ShopifyMetafieldDefinitionOption[]>([]);
    const [status, setStatus] = useState<DataSyncStatus | undefined>(undefined);
    const [form, setForm] = useState<AttributeForm>(emptyForm);
    const [selectedDefinitionKey, setSelectedDefinitionKey] = useState("");
    const [attributeModalOpen, setAttributeModalOpen] = useState(false);
    const [attributeModalMode, setAttributeModalMode] = useState<AttributeModalMode>("add");
    const [manualOpen, setManualOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const [definitionError, setDefinitionError] = useState<string | undefined>(undefined);
    const [success, setSuccess] = useState<string | undefined>(undefined);

    const loadData = useCallback(async () => {
        setLoading(true);

        try {
            let definitionsFailed = false;
            const definitionsRequest = agent.ShopifyMetafieldAttributes.definitions().catch(() => {
                definitionsFailed = true;
                return [] as ShopifyMetafieldDefinitionOption[];
            });

            const [loadedAttributes, loadedStatus, loadedDefinitions] = await Promise.all([
                agent.ShopifyMetafieldAttributes.list({ enabledOnly: false, includeValues: false }),
                agent.Settings.dataSyncStatus(),
                definitionsRequest,
            ]);

            const nextAttributes = cloneAttributes(loadedAttributes);
            setAttributes(nextAttributes);
            setBaselineAttributes(cloneAttributes(nextAttributes));
            setStatus(loadedStatus);
            setDefinitions(loadedDefinitions);
            setDefinitionError(definitionsFailed ? "Existing Shopify metafields could not be loaded. Use manual linking if needed." : undefined);
            setError(undefined);
        } catch {
            setError("Attributes could not be loaded.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const hasUnsavedChanges = useMemo(
        () => attributeSnapshot(attributes) !== attributeSnapshot(baselineAttributes),
        [attributes, baselineAttributes]
    );

    const enabledPathKeys = useMemo(
        () => new Set(attributes.filter((attribute) => attribute.isEnabled).map((attribute) => pathKey(attribute.ownerType, attribute.namespace, attribute.key))),
        [attributes]
    );

    const attributeByPath = useMemo(() => {
        const map = new Map<string, DraftAttribute>();
        attributes.forEach((attribute) => {
            map.set(pathKey(attribute.ownerType, attribute.namespace, attribute.key), attribute);
        });
        return map;
    }, [attributes]);

    const baselineByIdentity = useMemo(() => {
        const map = new Map<string, string>();
        baselineAttributes.forEach((attribute) => {
            map.set(attributeIdentity(attribute), JSON.stringify(normalizeAttributeSnapshot(attribute)));
        });
        return map;
    }, [baselineAttributes]);

    const definitionOptions = useMemo(
        () => [
            { label: definitions.length === 0 ? "No Shopify metafields found" : "Select a Shopify metafield", value: "" },
            ...definitions.map((definition) => {
                const key = pathKey(definition.ownerType, definition.namespace, definition.key);
                const enabled = enabledPathKeys.has(key);
                return {
                    label: `${ownerTypeLabel(definition.ownerType)} - ${definition.name || `${definition.namespace}.${definition.key}`}${enabled ? " (enabled)" : ""}`,
                    value: key,
                };
            }),
        ],
        [definitions, enabledPathKeys]
    );

    const selectedDefinition = definitions.find(
        (definition) => pathKey(definition.ownerType, definition.namespace, definition.key) === selectedDefinitionKey
    );
    const selectedDefinitionExistingAttribute = selectedDefinition ? attributeByPath.get(selectedDefinitionKey) : undefined;
    const selectedDefinitionAlreadyEnabled = !!selectedDefinitionExistingAttribute?.isEnabled;
    const enabledAttributeCount = attributes.filter((attribute) => attribute.isEnabled).length;
    const hasAttributes = attributes.length > 0;
    const nextDisplayOrder = Math.max(0, ...attributes.map((attribute) => attribute.displayOrder ?? 0)) + 10;

    const resetModal = useCallback(() => {
        setAttributeModalOpen(false);
        setAttributeModalMode("add");
        setSelectedDefinitionKey("");
        setManualOpen(false);
        setForm(emptyForm);
        setError(undefined);
    }, []);

    const openAddAttributeModal = useCallback(() => {
        setAttributeModalMode("add");
        setForm({ ...emptyForm, displayOrder: String(nextDisplayOrder) });
        setSelectedDefinitionKey("");
        setManualOpen(false);
        setAttributeModalOpen(true);
        setError(undefined);
        setSuccess(undefined);
    }, [nextDisplayOrder]);

    const openEditAttributeModal = useCallback((attribute: DraftAttribute) => {
        setAttributeModalMode("edit");
        setForm(attributeToForm(attribute));
        setSelectedDefinitionKey("");
        setManualOpen(true);
        setAttributeModalOpen(true);
        setError(undefined);
        setSuccess(undefined);
    }, []);

    const toggleAttribute = useCallback((attribute: DraftAttribute, isEnabled: boolean) => {
        setSuccess(undefined);
        setAttributes((current) => current.map((currentAttribute) =>
            attributeIdentity(currentAttribute) === attributeIdentity(attribute)
                ? { ...currentAttribute, isEnabled }
                : currentAttribute
        ));
    }, []);

    const addSelectedDefinitionToDraft = useCallback(() => {
        if (!selectedDefinition) {
            setError("Select a Shopify metafield first.");
            return;
        }

        if (selectedDefinitionAlreadyEnabled) {
            setError("That metafield is already connected.");
            return;
        }

        const nextAttribute = definitionToDraftAttribute(selectedDefinition, selectedDefinitionExistingAttribute, nextDisplayOrder);
        setAttributes((current) => upsertDraftAttribute(current, nextAttribute));
        resetModal();
    }, [
        nextDisplayOrder,
        resetModal,
        selectedDefinition,
        selectedDefinitionAlreadyEnabled,
        selectedDefinitionExistingAttribute,
    ]);

    const saveManualAttributeToDraft = useCallback(() => {
        const payload = formToPayload(form);
        if (!payload.namespace || !payload.key) {
            setError("Namespace and key are required.");
            return;
        }

        const duplicate = attributes.find((attribute) =>
            attribute.id !== payload.id
            && pathKey(attribute.ownerType, attribute.namespace, attribute.key) === pathKey(payload.ownerType, payload.namespace, payload.key)
        );

        if (duplicate) {
            setError("That metafield is already linked as an attribute.");
            return;
        }

        const existingAttribute = payload.id
            ? attributes.find((attribute) => attribute.id === payload.id)
            : attributeByPath.get(pathKey(payload.ownerType, payload.namespace, payload.key));

        setAttributes((current) => upsertDraftAttribute(current, payloadToDraftAttribute(payload, existingAttribute)));
        resetModal();
    }, [attributeByPath, attributes, form, resetModal]);

    const handleModalPrimaryAction = useCallback(() => {
        if (attributeModalMode === "edit" || manualOpen) {
            saveManualAttributeToDraft();
            return;
        }

        addSelectedDefinitionToDraft();
    }, [addSelectedDefinitionToDraft, attributeModalMode, manualOpen, saveManualAttributeToDraft]);

    const discardChanges = useCallback(() => {
        setAttributes(cloneAttributes(baselineAttributes));
        resetModal();
        setError(undefined);
        setSuccess(undefined);
    }, [baselineAttributes, resetModal]);

    const saveChanges = useCallback(async () => {
        if (!hasUnsavedChanges) return;

        const changedAttributes = attributes.filter((attribute) =>
            baselineByIdentity.get(attributeIdentity(attribute)) !== JSON.stringify(normalizeAttributeSnapshot(attribute))
        );

        setSaving(true);
        setError(undefined);
        setSuccess(undefined);

        try {
            for (const attribute of changedAttributes) {
                await agent.ShopifyMetafieldAttributes.upsert(attributeToPayload(attribute));
            }

            await loadData();
            setSuccess("Product attributes saved.");
        } catch {
            setError("Product attributes could not be saved.");
        } finally {
            setSaving(false);
        }
    }, [attributes, baselineByIdentity, hasUnsavedChanges, loadData]);

    useEffect(() => {
        onStatusChange?.({
            hasChanges: hasUnsavedChanges,
            loading,
            saving,
            save: saveChanges,
            discard: discardChanges,
        });
    }, [discardChanges, hasUnsavedChanges, loading, onStatusChange, saveChanges, saving]);

    const modalPrimaryLabel = attributeModalMode === "edit"
        ? "Apply changes"
        : manualOpen
            ? "Apply changes"
            : "Connect metafield";

    const modalPrimaryDisabled = saving
        || (attributeModalMode === "add" && !manualOpen && (!selectedDefinition || selectedDefinitionAlreadyEnabled))
        || ((attributeModalMode === "edit" || manualOpen) && (!form.namespace.trim() || !form.key.trim()));

    const rows = attributes.map((attribute, index) => (
        <IndexTable.Row id={attributeIdentity(attribute)} key={attributeIdentity(attribute)} position={index}>
            <IndexTable.Cell>
                <Checkbox
                    label={`Use ${attribute.name} in app`}
                    labelHidden
                    checked={attribute.isEnabled}
                    disabled={saving}
                    onChange={(checked) => toggleAttribute(attribute, checked)}
                />
            </IndexTable.Cell>
            <IndexTable.Cell>
                <BlockStack gap="050">
                    <Text as="span" fontWeight="semibold">
                        {attribute.name || `${attribute.namespace}.${attribute.key}`}
                    </Text>
                    {attribute.description ? (
                        <Text as="span" variant="bodySm" tone="subdued">
                            {attribute.description}
                        </Text>
                    ) : null}
                </BlockStack>
            </IndexTable.Cell>
            <IndexTable.Cell>
                <BlockStack gap="050">
                    <Text as="span">{ownerTypeLabel(attribute.ownerType)}</Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                        {attribute.namespace}.{attribute.key}
                    </Text>
                </BlockStack>
            </IndexTable.Cell>
            <IndexTable.Cell>{attribute.valueCount}</IndexTable.Cell>
            <IndexTable.Cell>{formatDateTime(attribute.lastIndexedAt)}</IndexTable.Cell>
            <IndexTable.Cell>
                <Button variant="plain" onClick={() => openEditAttributeModal(attribute)} disabled={saving}>
                    Edit
                </Button>
            </IndexTable.Cell>
        </IndexTable.Row>
    ));

    if (loading && attributes.length === 0) {
        return (
            <SettingsCard title="Product attributes">
                <InlineStack gap="200" align="center">
                    <Spinner accessibilityLabel="Loading product attributes" size="small" />
                    <Text as="span">Loading product attributes...</Text>
                </InlineStack>
            </SettingsCard>
        );
    }

    return (
        <>
            <BlockStack gap="400">
                {!hasAttributes ? (
                    <Card>
                        <Box padding="400">
                            <BlockStack gap="400">
                                {error ? <Banner tone="critical">{error}</Banner> : null}
                                {success ? <Banner tone="success">{success}</Banner> : null}

                                <EmptyState
                                    heading="No product attributes connected yet"
                                    image={productAttributesEmptyStateImage}
                                    action={{
                                        content: "Connect metafield",
                                        onAction: openAddAttributeModal,
                                        disabled: saving,
                                    }}
                                    footerContent={
                                        <p>
                                            After connecting metafields, use{" "}
                                            {onOpenShopifySync ? (
                                                <Link onClick={onOpenShopifySync}>
                                                    Shopify sync
                                                </Link>
                                            ) : (
                                                "Shopify sync"
                                            )}{" "}
                                            to cache their latest values.
                                        </p>
                                    }
                                >
                                    <p>
                                        Connect Shopify metafields to use their values in Product Guides, filters, and storefront display.
                                    </p>
                                </EmptyState>
                            </BlockStack>
                        </Box>
                    </Card>
                ) : (
                    <SettingsCard
                        title="Connected attributes"
                        description="These Shopify metafields are linked to the app. The app only syncs values from the metafields listed below."
                    >
                        <BlockStack gap="400">
                            {error ? <Banner tone="critical">{error}</Banner> : null}
                            {success ? <Banner tone="success">{success}</Banner> : null}

                            <InlineStack align="space-between" gap="400" blockAlign="center">
                                <Text as="span" tone="subdued">
                                    Connected attributes: {enabledAttributeCount} {"\u00b7"} Cached attribute values: {status?.syncedAttributeValueCount ?? 0}
                                </Text>
                            </InlineStack>

                            <IndexTable
                                resourceName={{ singular: "attribute", plural: "attributes" }}
                                itemCount={attributes.length}
                                selectable={false}
                                headings={[
                                    { title: "Use in app" },
                                    { title: "Display name" },
                                    { title: "Connected metafield" },
                                    { title: "Cached values" },
                                    { title: "Last synced" },
                                    { title: "Actions" },
                                ]}
                            >
                                {rows}
                            </IndexTable>

                            <InlineStack align="start" blockAlign="center" gap="200">
                                <Button icon={PlusIcon} onClick={openAddAttributeModal} disabled={saving}>
                                    Connect metafield
                                </Button>
                            </InlineStack>
                            <Text as="p" variant="bodySm" tone="subdued">
                                To update cached attribute values, use{" "}
                                {onOpenShopifySync ? (
                                    <Button variant="plain" onClick={onOpenShopifySync}>
                                        Shopify sync
                                    </Button>
                                ) : (
                                    "Shopify sync"
                                )}
                                .
                            </Text>
                        </BlockStack>
                    </SettingsCard>
                )}
            </BlockStack>

            {attributeModalOpen ? (
                <div className="pf-field-modal" role="presentation">
                    <button
                        type="button"
                        className="pf-field-modal__backdrop"
                        aria-label="Close attribute settings"
                        onClick={resetModal}
                    />
                    <section
                        className="pf-field-modal__panel"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="pf-attribute-modal-title"
                    >
                        <Card padding="0">
                            <Box padding="400">
                                <InlineStack align="space-between" blockAlign="center" gap="300">
                                    <BlockStack gap="100">
                                        <Text as="h2" variant="headingMd" id="pf-attribute-modal-title">
                                            {attributeModalMode === "edit" ? "Edit connected metafield" : "Connect metafield"}
                                        </Text>
                                        <Text as="p" variant="bodySm" tone="subdued">
                                            {attributeModalMode === "edit"
                                                ? "Update how this Shopify metafield is linked to the app."
                                                : "Choose a Shopify metafield to connect to the app. Only connected metafields are synced by the app."}
                                        </Text>
                                    </BlockStack>
                                    <Button variant="tertiary" onClick={resetModal}>
                                        Cancel
                                    </Button>
                                </InlineStack>
                            </Box>
                            <Divider />
                            <Box padding="400">
                                <BlockStack gap="400">
                                    {definitionError && attributeModalMode === "add" ? <Banner tone="warning">{definitionError}</Banner> : null}

                                    {attributeModalMode === "add" ? (
                                        <>
                                            <FormLayout>
                                                <Select
                                                    label="Existing Shopify metafield"
                                                    options={definitionOptions}
                                                    value={selectedDefinitionKey}
                                                    onChange={setSelectedDefinitionKey}
                                                    disabled={definitions.length === 0 || manualOpen}
                                                />
                                            </FormLayout>

                                            {selectedDefinition && !manualOpen ? (
                                                <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                                                    <BlockStack gap="100">
                                                        <Text as="p" fontWeight="semibold">
                                                            {selectedDefinition.namespace}.{selectedDefinition.key}
                                                        </Text>
                                                        <Text as="p" tone="subdued">
                                                            {ownerTypeLabel(selectedDefinition.ownerType)}
                                                        </Text>
                                                        {selectedDefinition.description ? (
                                                            <Text as="p" tone="subdued">
                                                                {selectedDefinition.description}
                                                            </Text>
                                                        ) : null}
                                                    </BlockStack>
                                                </Box>
                                            ) : null}

                                            <Divider />

                                            <InlineStack align="space-between" blockAlign="center" gap="300">
                                                <BlockStack gap="100">
                                                    <Text as="h3" variant="headingSm">
                                                        Manual connection
                                                    </Text>
                                                    <Text as="p" tone="subdued">
                                                        Use this only when a metafield does not appear in the picker.
                                                    </Text>
                                                </BlockStack>
                                                <Button
                                                    onClick={() => setManualOpen((open) => !open)}
                                                    ariaExpanded={manualOpen}
                                                    ariaControls="manual-attribute-link"
                                                >
                                                    {manualOpen ? "Use metafield picker" : "Enter manually"}
                                                </Button>
                                            </InlineStack>
                                        </>
                                    ) : null}

                                    <Collapsible id="manual-attribute-link" open={manualOpen || attributeModalMode === "edit"}>
                                        <BlockStack gap="400">
                                            <FormLayout>
                                                <FormLayout.Group>
                                                    <Select
                                                        label="Owner"
                                                        options={ownerTypeOptions}
                                                        value={form.ownerType}
                                                        onChange={(value) => setForm((current) => ({ ...current, ownerType: value as ShopifyMetafieldAttributeOwnerType }))}
                                                    />
                                                    <TextField
                                                        label="Namespace"
                                                        value={form.namespace}
                                                        onChange={(value) => setForm((current) => ({ ...current, namespace: value }))}
                                                        autoComplete="off"
                                                    />
                                                    <TextField
                                                        label="Key"
                                                        value={form.key}
                                                        onChange={(value) => setForm((current) => ({ ...current, key: value }))}
                                                        autoComplete="off"
                                                    />
                                                </FormLayout.Group>
                                                <FormLayout.Group>
                                                    <TextField
                                                        label="Display name"
                                                        value={form.name}
                                                        onChange={(value) => setForm((current) => ({ ...current, name: value }))}
                                                        autoComplete="off"
                                                    />
                                                    <TextField
                                                        label="Display order"
                                                        value={form.displayOrder}
                                                        onChange={(value) => setForm((current) => ({ ...current, displayOrder: value }))}
                                                        autoComplete="off"
                                                        type="number"
                                                    />
                                                </FormLayout.Group>
                                                <TextField
                                                    label="Description"
                                                    value={form.description}
                                                    onChange={(value) => setForm((current) => ({ ...current, description: value }))}
                                                    autoComplete="off"
                                                    multiline={3}
                                                />
                                                <Checkbox
                                                    label="Use in app"
                                                    checked={form.isEnabled}
                                                    onChange={(checked) => setForm((current) => ({ ...current, isEnabled: checked }))}
                                                />
                                            </FormLayout>
                                        </BlockStack>
                                    </Collapsible>
                                </BlockStack>
                            </Box>
                            <Divider />
                            <Box padding="400">
                                <InlineStack align="end" gap="200">
                                    <Button onClick={resetModal} disabled={saving}>
                                        Cancel
                                    </Button>
                                    <Button variant="primary" onClick={handleModalPrimaryAction} disabled={modalPrimaryDisabled}>
                                        {selectedDefinitionAlreadyEnabled && attributeModalMode === "add" && !manualOpen
                                            ? "Already connected"
                                            : modalPrimaryLabel}
                                    </Button>
                                </InlineStack>
                            </Box>
                        </Card>
                    </section>
                </div>
            ) : null}
        </>
    );
};

export default MetafieldAttributesSettings;
